import {Auth, Principal} from "@codeArt/auth";
import {textContent} from "@codeArt/utils/dom";
import React, {useEffect, useMemo, useState} from "react";
import {createIcon} from "@codeArt/components/common";
import Tooltip from "@codeArt/components/Tooltip";
import HelpOutlineIcon from "@material-ui/icons/HelpOutline";
import ChevronRightIcon from "@material-ui/icons/ChevronRight";
import {getThemeColor} from "@codeArt/components/theme/color";
import {useDispatch} from "react-redux";
import {empty, type} from "@codeArt/utils/common";
import {makeStyles} from "@material-ui/core/styles";
import EditOutlinedIcon from "@material-ui/icons/EditOutlined";
import DeleteOutlinedIcon from "@material-ui/icons/DeleteOutlined";
import ArrowRightAltIcon from '@material-ui/icons/ArrowRightAlt';
import clsx from "clsx";

const useStyles = makeStyles(theme => ({
  root: ({show, pointer, inline}) => ({
    marginBottom: 0,
    color: "#5f57c3",
    width: "max-content",
    // display: show && !inline ? "-webkit-box !important" : null,
    // display: show && !inline ? "-ms-flexbox !important" : null,
    display: show
      ? inline ? "inline !important" : "flex !important"
      : "none !important",
    cursor: pointer ? "pointer !important" : null
  }),
  boby: ({inline, href, weight}) => ({
    fontWeight: weight,
    display: !href && inline ? "inline !important" : null
  }),
  iconBase: () => ({
    /*    fontSize: "1.5rem !important", */ //和c-icon-s1  这种样式冲突
    lineHeight: 1,
    display: "inline-block",
    font: "normal normal normal 14px/1",
    textRendering: "auto",
    WebkitFontSmoothing: "antialiased",
    MozOsxFontSmoothing: "grayscale"

    /*     [theme.breakpoints.up('lg')]: {//和c-icon-s1  这种样式冲突
          fontSize: "1.25rem !important",
        }, */
  }),
  leftIcon: ({children}) => ({
    marginRight: children ? " 0.25rem !important" : ""
  }),
  rightIcon: ({children}) => ({
    marginLeft: children ? " 0.25rem !important" : ""
  })
}));

const linkSizeClassName = {
  xs: {
    text: "c-text-s1",
    icon: "c-icon-s1"
  },
  sm: {
    text: "c-text-s1d5",
    icon: "c-icon-s1d5"
  },
  md: {
    text: "c-text-s2",
    icon: "c-icon-s2"
  }
};

export const Link = ({
  tooltip,
  hidden,
  disabled,
  permission,
  role,
  dark,
  color,
  leftIcon,
  leftIconClassName,
  leftIconStyle,
  rightIcon,
  rightIconStyle,
  rightIconClassName,
  className,
  textClassName,
  weight,
  size,
  onClick,
  children,
  sizeClassName,
  href,
  inline,
  ...ps
}) => {
  const dispatch = useDispatch();
  tooltip = useMemo(() => parseTooltip(tooltip), [ tooltip ]);
  const [ verifyMessage, setVerifyMessage ] = useState("");
  const [ _hidden, setHidden ] = useState(hidden);
  const [ _disabled, setDisabled ] = useState(disabled);

  const classes = useStyles({
    show: !_hidden,
    // pointer: onClick && !_disabled, 不知道为什么，就是无效
    pointer:true,
    inline,
    weight,
    children,
    href
  });
  sizeClassName = sizeClassName || linkSizeClassName[size];
  color = _disabled ? "m-text-muted" : getThemeColor(color);
  const rawDisabled = disabled;

  function parseTooltip(t){
    t = t || {};
    if (type(t) === "string") return {description: t, placement: "top"};
    if (!t.placement) t.placement = "top";
    return t;
  }

  const getTooltipDescription = () => {
    if (_disabled && tooltip.disabled) return tooltip.disabled;
    if (verifyMessage) return verifyMessage;
    return tooltip.description;
  };

  const verify = () => {
    setDisabled(true);
    setVerifyMessage("正在效验权限");
    Principal.verify({permission, role, dark},dispatch).then(({allow,dark,message})=>{
      if(allow){
        setDisabled(rawDisabled);
        setVerifyMessage("");
      }else{
        if(dark){
          setHidden(true); //由于灰度原因无法访问
          return;
        }
        setDisabled(true);
        setVerifyMessage(message||"无此权限");
      }
    });
  };

  useEffect(() => {
    verify();
  }, []);

  if (_hidden) return null;

  const tooltipWrapper = content => {
    const description = getTooltipDescription();
    return Tooltip({
      description,
      placement: tooltip.placement,
      content: content
    });
  };

  const getText = children => {
    if (ps["data-gta-title"]) return ps["data-gta-title"];
    return textContent(children);
  };

  if (leftIcon)
    leftIcon = createIcon(
      leftIcon,
      clsx(
        classes.iconBase,
        classes.leftIcon,
        color,
        sizeClassName.icon,
        leftIconClassName
      ),
      {style: leftIconStyle}
    );
  if (rightIcon)
    rightIcon = createIcon(
      rightIcon,
      clsx(
        classes.iconBase,
        classes.rightIcon,
        color,
        sizeClassName.icon,
        rightIconClassName
      ),
      {style: rightIconStyle}
    );

  if (!href) {
    return tooltipWrapper(
      <div
        data-gta-container="m"
        data-gta-title={getText(children)}
        className={clsx(
          "flex-row justify-content-center align-items-center",
          classes.root,
          className
        )}
        onClick={e => {
          if (onClick && !_disabled) onClick(e);
        }}
      >
        {leftIcon}
        {!empty(children) ? (
          <div
            className={clsx(
              "ml-auto c-text-truncate c-link c-lh-1",
              classes.boby,
              color,
              sizeClassName.text,
              textClassName
            )}
            {...ps}
          >
            {children}
          </div>
        ) : null}
        {rightIcon}
      </div>
    );
  }

  return tooltipWrapper(
    <div
      className={clsx(
        "flex-row justify-content-center align-items-center",
        classes.root,
        className
      )}
    >
      {
        <a
          href={href}
          target="_blank"
          className={clsx(
            "ml-auto c-text-truncate c-link c-lh-1",
            classes.boby,
            color,
            sizeClassName.text,
            textClassName
          )}
          {...ps}
        >
          {leftIcon}
          {children}
          {rightIcon}
        </a>
      }
    </div>
  );
};

Link.defaultProps = {
  weight: "normal",
  className: "",
  size: "sm",
  textClassName: "",
  leftIconClassName: "",
  rightIconClassName: "",
  inline: false,
  disabled: false
};

Link.getSizeClassName = size => {
  return linkSizeClassName[size];
};

export class HelpLink extends React.Component {
  constructor(props) {
    super(props);
  }

  render() {
    let {
      onClick,
      children,
      className,
      size,
      href,
      textClassName,
      leftIconClassName,
      tooltip
    } = this.props;
    let ext = {};

    if (onClick) {
      ext.onClick = () => {
        onClick();
      };
    }
    return (
      <Link
        data-gta-title={this.props["data-gta-title"] || this.props["title"]}
        tooltip={tooltip}
        className={className}
        size={size}
        href={href}
        textClassName={textClassName}
        leftIcon={HelpOutlineIcon}
        leftIconClassName={leftIconClassName}
        {...ext}
      >
        {children}
      </Link>
    );
  }
}

export function CellLink(props){
  //专为表格使用的link
  let {icon} = props;
  let p = {...props};
  delete p.icon;
  return <Link leftIcon={icon} size={"xs"} {...p} />;
}

export function GoCellLink(props){
  //专为表格使用的link
  return <CellLink icon={ChevronRightIcon} {...props} />;
}

export function EditLink(props){
  let ps = getProps(props, EditOutlinedIcon);
  return (
    <Link leftIcon={EditOutlinedIcon} size={"sm"} {...ps}>
      {props.children || "编辑"}
    </Link>
  );
}

export function DeleteLink(props){
  return (
    <Link color="danger" leftIcon={DeleteOutlinedIcon} size={"sm"} {...props}>
      {props.children || "删除"}
    </Link>
  );
}

export function DetailLink(props) {
  return <Link rightIcon={ArrowRightAltIcon} {...props}></Link>
}

const getProps = (props, icon) => {
  let ps = {...props};
  if (ps.right) {
    ps.rightIcon = icon;
    ps.leftIcon = null;
  }

  if (ps.left) {
    ps.rightIcon = null;
    ps.leftIcon = icon;
  }

  delete ps.right;
  delete ps.left;

  return ps;
};
